<template>
  <div class="profile-edit-menu relative flex flex-col z-[1]">
    <Button
      class="max-w-[270px] justify-between"
      variant="outlined"
      size="small"
      :value="dropdownText"
      :aria-expanded="`${buttonToggled}`"
      @click.prevent="toggleMenu"
    >
      <CaretIcon :class="iconDirection" color="#4B4C52" />
    </Button>

    <Card v-if="buttonToggled" class="max-w-[270px] w-full top-36">
      <p class="text-overline capitalize pb-8">My services</p>
      <MenuLinkList
        :items="completedProfileItems"
        :filter-by-visible="false"
        list-class="profile-edit-menu__my-services"
        list-variant="action-menu"
        item-class="flex items-center text-subtitle font-bold border-2 border-white py-8 px-0"
        :icon-size="32"
        icon-color="#4B4C52"
      >
        <template #item-right="{ item }">
          <password-hide-outline-icon v-if="!item.visible" class="ml-auto" :width="16" :height="16" color="#8A8A8F" />
        </template>
      </MenuLinkList>

      <MenuLinkList
        v-if="incompleteProfileItems.length"
        :items="incompleteProfileItems"
        list-class="flex flex-col gap-16"
        item-class="flex items-center gap-8 bg-grey-background-contrast rounded-xl pl-12 py-8"
        :icon-size="32"
        icon-color="#6E6F74"
      />
    </Card>
  </div>
</template>

<script lang="ts">
// Components
import Button from '@/components/button/Button.vue';
import Card from '@/components/card/Card.vue';
import MenuLinkList from '@/components/menu_link_list/MenuLinkList.vue';

// Icons
import CaretIcon from '@/graphics/icons/CaretIcon.vue';
import BabysitterIllustration from '@/graphics/illustrations/BabysitterIllustration.vue';
import NannyIllustration from '@/graphics/illustrations/NannyIllustration.vue';
import PetCareIllustration from '@/graphics/illustrations/PetCareIllustration.vue';
import SeniorCareIllustration from '@/graphics/illustrations/SeniorCareIllustration.vue';
import HousekeepingIllustration from '@/graphics/illustrations/HousekeepingIllustration.vue';
import PasswordHideOutlineIcon from '@/graphics/icons/PasswordHideOutlineIcon.vue';
import AddOutlineIcon from '@/graphics/icons/AddOutlineIcon.vue';

// GraphQL
import { sendSegmentEvent } from '@/lib/segment';
import { writeDomainCookie } from '@/lib/domain_cookie';
import getNewProfileRoute from '@/helpers/profileRouteHelper';

export default {
  name: 'ProfileEditMenu',
  components: {
    Button,
    Card,
    MenuLinkList,
    CaretIcon,
    PasswordHideOutlineIcon,
    /* eslint-disable vue/no-unused-components -- icons are passed as constructors to MenuLinkList; not referenced in this template */
    BabysitterIllustration,
    NannyIllustration,
    PetCareIllustration,
    SeniorCareIllustration,
    HousekeepingIllustration,
    AddOutlineIcon,
    /* eslint-enable vue/no-unused-components */
  },
  props: {
    careTypeForLabel: {
      type: String,
      default: '',
      required: false,
    },
    profileAccountData: {
      type: Object,
      required: true,
    },
  },
  data(): any {
    return {
      buttonToggled: false,
    };
  },
  computed: {
    dropdownText(): string {
      return this.profileData.find((profile) => profile.type === this.careTypeForLabel)?.displayName || 'Edit profile';
    },
    iconDirection(): string {
      return this.buttonToggled ? 'transform rotate-180' : '';
    },
    profileData(): any {
      return [
        {
          type: 'babysitter',
          displayName: 'babysitter',
          complete: this.profileAccountData?.profiles?.babysitter?.profileCompleted || false,
          visible: this.profileAccountData?.profiles?.babysitter?.profileVisible || false,
          icon: BabysitterIllustration,
        },
        {
          type: 'nanny',
          displayName: 'nanny',
          complete: this.profileAccountData?.profiles?.nanny?.profileCompleted || false,
          visible: this.profileAccountData?.profiles?.nanny?.profileVisible || false,
          icon: NannyIllustration,
        },
        {
          type: 'pet_care',
          displayName: 'pet care',
          complete: this.profileAccountData?.profiles?.petCare?.profileCompleted || false,
          visible: this.profileAccountData?.profiles?.petCare?.profileVisible || false,
          icon: PetCareIllustration,
        },
        {
          type: 'companion_care',
          displayName: 'companion care',
          complete: this.profileAccountData?.profiles?.companionCare?.profileCompleted || false,
          visible: this.profileAccountData?.profiles?.companionCare?.profileVisible || false,
          icon: SeniorCareIllustration,
        },
        {
          type: 'housekeeping',
          displayName: 'housekeeping',
          complete: this.profileAccountData?.profiles?.housekeeping?.profileCompleted || false,
          visible: this.profileAccountData?.profiles?.housekeeping?.profileVisible || false,
          icon: HousekeepingIllustration,
        },
      ];
    },
    completedProfiles(): any {
      return this.profileData.filter((profile) => profile.complete === true);
    },
    incompleteProfiles(): any {
      return this.profileData.filter((profile) => profile.complete === false);
    },
    completedProfileItems(): any {
      return this.completedProfiles.map((profile) => {
        const displayName = profile.displayName.charAt(0).toUpperCase() + profile.displayName.slice(1);
        return {
          ...profile,
          icon: profile.icon,
          label: displayName,
          href: this.viewProfilePath(profile.type),
          ariaLabel: `${!profile.visible ? 'Hidden' : ''} ${displayName} profile`,
        };
      });
    },
    incompleteProfileItems(): any {
      return this.incompleteProfiles.map((profile) => ({
        icon: AddOutlineIcon,
        label: `Add ${profile.displayName}`,
        labelClasses: 'text-subtitle !font-medium mb-4 text-grey-text-subdued',
        to: this.getNewProfileRoute(profile.type),
        click: this.handleAddProfileClick,
      }));
    },
    profileOptionButtonClasses(): string {
      return 'text-xs !tracking-normal !leading-none font-regular normal-case primary-focus !p-0';
    },
  },
  mounted() {
    document.addEventListener(
      'click',
      ({ target }) => {
        if ((target as HTMLElement).closest('.profile-edit-menu, .adventure-active-modal') !== null) return;
        this.buttonToggled = false;
      },
      false
    );
  },
  methods: {
    toggleMenu(): void {
      this.buttonToggled = !this.buttonToggled;
    },
    handleEditProfileClick(type: string): void {
      sendSegmentEvent('Sitter Profile Edit Profile Clicked', {
        care_type: type,
      });
    },
    handleAddProfileClick(): void {
      writeDomainCookie('additional_profile', 'true');
    },
    viewProfilePath(type): string {
      const { id } = this.profileAccountData;
      return `/provider/profile/${type}/${id}`;
    },
    getNewProfileRoute(type): { path: string; query: { additionalProfile: string; careType: string } } {
      return getNewProfileRoute(type);
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  position: absolute !important;
}
</style>
